import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TagIcon from '@mui/icons-material/Tag';
import { Alert, Button, Divider, Paper, Stack, TextField, Typography } from '@mui/material';
import React, { useState } from 'react';

function EditContract({ contractData, parsedData, fileName, onBack, onSave }) {
    const parsedContract = parsedData?.contract ?? null;

    const resolveValue = (savedValue, parsedValue) => {
        if (savedValue !== null && savedValue !== undefined && savedValue !== '') {
            return savedValue;
        }
        return parsedValue ?? '';
    };

    const [externalContractId, setExternalContractId] = useState(resolveValue(contractData?.external_id, parsedContract?.contract_number));
    const [name, setName] = useState(resolveValue(contractData?.name, parsedContract?.contract_name));
    const [startDate, setStartDate] = useState(resolveValue(contractData?.start_date, parsedContract?.contract_start_date));
    const [effectiveStartDate, setEffectiveStartDate] = useState(resolveValue(contractData?.effective_start_date, parsedContract?.effective_start_date));
    const [endDate, setEndDate] = useState(resolveValue(contractData?.end_date, parsedContract?.contract_end_date));
    const [error, setError] = useState(null);

    const handleSave = () => {
        setError(null);
        if (!name.trim()) {
            setError('Contract name is required.');
            return;
        }
        onSave({ external_id: externalContractId, name, start_date: startDate, effective_start_date: effectiveStartDate, end_date: endDate });
    };

    return (
        <Stack spacing={4} sx={{ mt: 4, pb: 8 }}>
            {/* Header row */}
            <Stack direction='row' justifyContent='space-between' alignItems='flex-start'>
                <Stack spacing={0.5}>
                    <Typography variant='heavy' sx={{ fontSize: '24px' }}>
                        Review Contract Details
                    </Typography>
                    <Typography sx={{ color: 'subtext.main', fontSize: '14px' }}>
                        Verify or edit the extracted fields below.
                    </Typography>
                </Stack>
                {fileName && (
                    <Stack
                        direction='row'
                        spacing={1}
                        alignItems='center'
                        sx={{
                            border: '1.5px solid',
                            borderColor: 'green.main',
                            borderRadius: 6,
                            px: 2,
                            py: 0.75,
                        }}
                    >
                        <CheckCircleOutlineIcon sx={{ color: 'green.main', fontSize: 18 }} />
                        <Typography sx={{ color: 'green.main', fontSize: '13px', fontWeight: 600 }}>
                            Parsed from {fileName}
                        </Typography>
                    </Stack>
                )}
            </Stack>

            {error && <Alert severity='error'>{error}</Alert>}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems='stretch'>
                <Paper elevation={0} sx={{ borderRadius: 2, p: 3, flex: 1, backgroundColor: 'action.hover', opacity: 0.8 }}>
                    <SectionHeader
                        icon={<TagIcon sx={{ color: 'subtext.secondary', fontSize: 20 }} />}
                        label='Parsed Contract'
                    />
                    <Divider sx={{ mb: 3 }} />
                    <Stack spacing={3}>
                        <DetailField
                            label='External Contract ID'
                            value={parsedContract?.contract_number ?? ''}
                            readOnly
                        />
                        <DetailField
                            label='Contract Name'
                            value={parsedContract?.contract_name ?? ''}
                            readOnly
                        />
                        <Stack direction='row' spacing={3}>
                            <DetailField
                                label='Start Date'
                                value={parsedContract?.contract_start_date ?? ''}
                                readOnly
                                type='date'
                            />
                            <DetailField
                                label='Effective Start Date'
                                value={parsedContract?.effective_start_date ?? ''}
                                readOnly
                                type='date'
                            />
                            <DetailField
                                label='End Date'
                                value={parsedContract?.contract_end_date ?? ''}
                                readOnly
                                type='date'
                            />
                        </Stack>
                    </Stack>
                </Paper>

                <Paper elevation={1} sx={{ borderRadius: 2, p: 3, flex: 1 }}>
                    <SectionHeader
                        icon={<CalendarTodayIcon sx={{ color: 'subtext.secondary', fontSize: 20 }} />}
                        label='Saved Contract'
                    />
                    <Divider sx={{ mb: 3 }} />
                    <Stack spacing={3}>
                        <DetailField
                            label='External Contract ID'
                            value={externalContractId}
                            onChange={(e) => setExternalContractId(e.target.value)}
                        />
                        <DetailField
                            label='Contract Name'
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                        <Stack direction='row' spacing={3}>
                            <DetailField
                                label='Start Date'
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                type='date'
                            />
                            <DetailField
                                label='Effective Start Date'
                                value={effectiveStartDate}
                                onChange={(e) => setEffectiveStartDate(e.target.value)}
                                type='date'
                            />
                            <DetailField
                                label='End Date'
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                type='date'
                            />
                        </Stack>
                    </Stack>
                </Paper>
            </Stack>

            {/* Actions */}
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Button variant='text' color='inherit' sx={{ color: 'subtext.main' }} onClick={onBack}>
                    ← Back
                </Button>
                <Button
                    variant='blueContained'
                    onClick={handleSave}
                >
                    Save Contract
                </Button>
            </Stack>
        </Stack>
    );
}

export default EditContract;

function SectionHeader({ icon, label }) {
    return (
        <Stack direction='row' spacing={1} alignItems='center' pb={1}>
            {icon}
            <Typography variant='heavy' sx={{ fontSize: '16px' }}>
                {label}
            </Typography>
        </Stack>
    );
}

function DetailField({ label, value, onChange, readOnly = false, type = 'text' }) {
    return (
        <Stack spacing={0.75} flex={1}>
            <Typography sx={{ fontSize: '13px', color: 'subtext.main' }}>
                {label}
            </Typography>
            <TextField
                type={type}
                value={value}
                onChange={onChange}
                fullWidth
                variant='outlined'
                size='small'
                InputLabelProps={type === 'date' ? { shrink: true } : undefined}
                InputProps={readOnly ? { readOnly: true } : undefined}
                disabled={readOnly}
                sx={readOnly ? {
                    '& .MuiInputBase-root': {
                        backgroundColor: 'action.hover',
                    },
                } : undefined}
            />
        </Stack>
    );
}